import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { APP_ROUTING_NAMES } from '../app.routes.names';
import { INavigationElement } from '../components/navigation-bar/INavigationElement';
import { NavigationBarComponent } from '../components/navigation-bar/navigation-bar.component';
import { APPLICATION_TYPES } from '../model/constants/application-types';
import { IApplication } from '../model/iapplication';
import { ICaseResponse } from '../model/service/icase-response';
import { AnsokanFormBuilderService } from '../service/ansokan-form-builder/ansokan-form-builder.service';
import { APPLICATION_NAMES } from '../service/ansokan-form-builder/application-names';
import { CustomValidators } from '../service/ansokan-form-builder/custom-validators';
import { AnsokanFormGroupService } from '../service/ansokan-form-group/ansokan-form-group.service';
import { AuthService } from '../service/auth/auth.service';
import { SubmissionService } from '../service/submission/submission.service';
import { ANSOKAN_ROUTING_NAMES } from './constants/ansokan.routes.names';
import { GET_PAGE_FLOW } from './constants/page-flows';

@Component({
    selector: 'app-ansokan',
    styleUrls: ['./ansokan.component.scss'],
    templateUrl: './ansokan.component.html',
    standalone: false
})
export class AnsokanComponent implements OnInit {

  public path: string;
  public pageFlow: INavigationElement[];
  public error: boolean;
  public sending: boolean;
  public authorized: boolean;
  @ViewChild(NavigationBarComponent) private navigation: NavigationBarComponent;

  constructor(
    private activated: ActivatedRoute,
    private router: Router,
    private change: ChangeDetectorRef,
    private formGroupService: AnsokanFormGroupService,
    private fb: AnsokanFormBuilderService,
    private submissionService: SubmissionService,
    private authService: AuthService,
  ) { }

  public get navigationEnabled(): boolean {
    return Boolean(this.navigation) && Boolean(this.pageFlow);
  }

  public get formValid(): boolean {
    return this.formGroupService.valid;
  }

  public get pathValid(): boolean {
    return Boolean(this.path) && Boolean(this.pageFlow) &&
      (this.pageFlow.map((p) => p.index).indexOf(this.path) !== -1 || this.isConfirmationPage);
  }

  public get hasNext(): boolean {
    return this.navigation ? this.navigation.hasNext() : this.isStartPage;
  }

  public get hasPrevious(): boolean {
    return this.navigation ? this.navigation.hasPrevious() : false;
  }

  public get isLastPage(): boolean {
    return !this.hasNext && this.hasPrevious;
  }

  public get isStartPage(): boolean {
    return this.path === ANSOKAN_ROUTING_NAMES.START;
  }

  public get isConfirmationPage(): boolean {
    return this.path === ANSOKAN_ROUTING_NAMES.CONFIRMATION;
  }

  public ngOnInit() {
    // Set autorized.
    this.authorized = Boolean(this.authService.getUser());
    // Add form controls.
    const group = this.fb.buildBaseForm();
    this.formGroupService.setFormGroup(group);
    // Listen to application type changes.
    const typeControl = group.get(APPLICATION_NAMES.APPLICATION_TYPE);
    typeControl.valueChanges.subscribe((type: string) => {
      this.pageFlow = GET_PAGE_FLOW(type);
      // Set page groups based on page flow.
      const pages = this.pageFlow ?
        this.pageFlow.map((page) => page.pageGroup).filter((pageGroup) => Boolean(pageGroup))
        : [];
      this.fb.setPages(group, pages);
      // set page group if first page has a page group.
      if (this.pageFlow && this.pageFlow[0].pageGroup) {
        this.setPageGroup(this.pageFlow[0].index);
      }
      // Add requires qualification validator for supplement applications.
      if (type === APPLICATION_TYPES.SUPPLEMENT_SWEDISH) {
        this.formGroupService.getFormGroup().setValidators(CustomValidators.requiresQualification);
      }
      this.change.detectChanges();
    });
    // Update type to set any loaded page flow.
    typeControl.updateValueAndValidity();
    // Add application value changes subscription.
    group.valueChanges.subscribe((value: IApplication) => this.fb.saveToStorage(value));
    // Get current page.
    this.activated.firstChild.url.subscribe((value: UrlSegment[]) => this.setPage(value));

  }

  public toNext(): void {
    if (this.formGroupService.pageValid) {
      const next = this.navigation.moveToNextIndex();
      this.navigateTo(next);
    } else {
      this.formGroupService.markPageGroupAsTouched();
    }
  }

  public toPrevious(): void {
    const previous = this.navigation.moveToPreviousIndex();
    this.navigateTo(previous);
  }

  public onSubmit(): void {
    if (!this.sending) {
      this.error = false;
      this.sending = true;
      const application = this.formGroupService.value;
      this.submissionService.submitApplication(application)
        .subscribe(
          (result: ICaseResponse) => {
            this.formGroupService.get(APPLICATION_NAMES.CASE_ID).setValue(result.diaryNumber, { emitEvents: false });
            this.formGroupService.get(APPLICATION_NAMES.APPLICANT_SSN).setValue(result.applicantSsn, { emitEvents: false });
            this.fb.clearStorage();
            this.navigateTo(ANSOKAN_ROUTING_NAMES.CONFIRMATION);
          },
          () => {
            this.error = true;
            this.sending = false;
          },
        );
    }
  }

  public closeApplication(): void {
    this.fb.clearStorage();
    this.router.navigate([APP_ROUTING_NAMES.START]);
  }

  private navigateTo(page: string): void {
    // Navigate to page.
    this.router.navigate([page], { relativeTo: this.activated });
    // Set page group.
    this.setPageGroup(page);
    // Cycle path to force re-drawing of navigation component.
    this.path = null;
    this.change.detectChanges();
    this.path = page;
    this.change.detectChanges();
  }

  private setPage(value: UrlSegment[]) {
    // Get current page from route path.
    this.path = value[0].path;
    this.setPageGroup(this.path);
    // Enable navigation bar to initialize.
    this.change.detectChanges();
  }

  private setPageGroup(page: string): void {
    if (this.pageFlow) {
      const group = this.pageFlow.filter((value) => value.index === page)
        .map((value) => value.pageGroup)
        .pop();
      this.formGroupService.setPageGroup(group);
    }
  }
}
